import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { Star, Minus, Plus, ShoppingBag, MessageCircle, Truck, Package, Sparkles } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [whatsapp, setWhatsapp] = useState("");
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, title: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`).then(async ({ data }) => {
      setProduct(data);
      setSelectedImage(0);
      const r = await api.get(`/reviews/${data.id}`);
      setReviews(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    api.get("/config").then(({ data }) => setWhatsapp(data.whatsapp)).catch(() => {});
  }, [slug]);

  const handleAddToCart = () => {
    addItem(product, quantity);
    toast.success(`Added ${quantity} × ${product.name} to cart`);
  };

  const handleWhatsappOrder = () => {
    const msg = encodeURIComponent(
      `Hi Ave_luxe_candles! I'd like to order:\n\n• ${product.name} (${quantity} × ₹${product.price})\n\nTotal: ₹${product.price * quantity}\n\nPlease confirm.`
    );
    window.open(`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}?text=${msg}`, "_blank");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.comment) {
      toast.error("Please fill in your name and comment");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        product_id: product.id,
        name: reviewForm.name,
        rating: reviewForm.rating,
        title: reviewForm.title,
        comment: reviewForm.comment,
      });
      toast.success("Thanks for sharing your review!");
      setReviewForm({ name: "", rating: 5, title: "", comment: "" });
      const r = await api.get(`/reviews/${product.id}`);
      setReviews(r.data);
      const pd = await api.get(`/products/${slug}`);
      setProduct(pd.data);
    } catch (err) {
      toast.error("Could not submit review");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="product-detail-loading">
        <div className="grid md:grid-cols-2 gap-12 animate-pulse">
          <div className="aspect-square bg-[#F3EFEA] rounded-lg" />
          <div className="space-y-4">
            <div className="h-4 bg-[#F3EFEA] w-24" />
            <div className="h-16 bg-[#F3EFEA]" />
            <div className="h-24 bg-[#F3EFEA]" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif-display text-4xl mb-4">Product not found</h2>
        <Link to="/shop" className="text-sm underline underline-offset-8 hover:text-[#D27D60]">Back to shop</Link>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Breadcrumb */}
        <div className="text-xs uppercase tracking-widest-plus text-[#9BA595] mb-8 space-x-2">
          <Link to="/" className="hover:text-[#D27D60]">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#D27D60]">Shop</Link>
          <span>/</span>
          <span className="text-[#1A1A1A]">{product.name}</span>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="aspect-square overflow-hidden rounded-lg bg-[#F3EFEA] mb-4">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-detail-main-image"
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-20 h-20 rounded overflow-hidden border-2 transition-colors ${
                      selectedImage === i ? "border-[#D27D60]" : "border-transparent"
                    }`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">
              ✦ {product.category.replace("-", " ")}
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mb-4">{product.name}</h1>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex gap-0.5">
                {[0,1,2,3,4].map(i => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-[#D27D60] text-[#D27D60]" : "text-[#E3DCD2]"}`}
                  />
                ))}
              </div>
              <span className="text-xs text-[#9BA595]">{product.rating.toFixed(1)} · {product.review_count} reviews</span>
            </div>

            <div className="flex items-baseline gap-3 mb-8">
              <span className="text-3xl font-medium" data-testid="product-price">₹{product.price}</span>
              {product.original_price && (
                <span className="text-lg text-[#9BA595] line-through">₹{product.original_price}</span>
              )}
              {product.original_price && (
                <span className="text-xs uppercase tracking-widest-plus bg-[#D27D60] text-[#FDFBF7] px-2 py-1 rounded-full">
                  Save {Math.round((1 - product.price / product.original_price) * 100)}%
                </span>
              )}
            </div>

            <p className="text-base leading-relaxed text-[#4a4a4a] mb-8">{product.description}</p>

            {product.features?.length > 0 && (
              <div className="mb-8 space-y-2">
                {product.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-1 h-1 rounded-full bg-[#D27D60]" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Quantity + CTA */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex items-center border border-[#E3DCD2] rounded-full">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="p-3 hover:text-[#D27D60] transition-colors"
                  data-testid="product-qty-decrease"
                >
                  <Minus className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <span className="px-4 text-sm font-medium min-w-[2rem] text-center" data-testid="product-qty-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(q => q + 1)}
                  className="p-3 hover:text-[#D27D60] transition-colors"
                  data-testid="product-qty-increase"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>

              <button
                onClick={handleAddToCart}
                className="flex-1 min-w-[200px] bg-[#1A1A1A] text-[#FDFBF7] px-6 py-3 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors flex items-center justify-center gap-2"
                data-testid="product-add-to-cart-btn"
              >
                <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
                Add to cart
              </button>
            </div>

            <button
              onClick={handleWhatsappOrder}
              className="w-full border border-[#25D366] text-[#25D366] px-6 py-3 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#25D366] hover:text-white transition-colors flex items-center justify-center gap-2 mb-8"
              data-testid="product-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              Order on WhatsApp
            </button>

            <div className="grid grid-cols-3 gap-3 pt-6 border-t border-[#E3DCD2]">
              {[
                { icon: Truck, label: "Free shipping", sub: "Above ₹999" },
                { icon: Package, label: "Ships in 2 days", sub: "From Howrah" },
                { icon: Sparkles, label: "Gift wrap", sub: "Complimentary" },
              ].map((f, i) => (
                <div key={i} className="text-center">
                  <f.icon className="w-4 h-4 mx-auto mb-2 text-[#D27D60]" strokeWidth={1.5} />
                  <div className="text-xs font-medium">{f.label}</div>
                  <div className="text-[10px] text-[#9BA595] uppercase tracking-widest-plus">{f.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews */}
        <section className="mt-24 grid md:grid-cols-12 gap-12" data-testid="product-reviews-section">
          <div className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Reviews</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-4">What people are saying</h2>
            <div className="flex items-center gap-3 mb-8">
              <div className="flex gap-0.5">
                {[0,1,2,3,4].map(i => (
                  <Star key={i} className={`w-5 h-5 ${i < Math.round(product.rating) ? "fill-[#D27D60] text-[#D27D60]" : "text-[#E3DCD2]"}`} />
                ))}
              </div>
              <span className="text-sm">{product.rating.toFixed(1)} · {product.review_count} reviews</span>
            </div>

            <form onSubmit={submitReview} className="space-y-4 p-6 bg-[#F3EFEA]/60 rounded-lg" data-testid="review-form">
              <div className="text-xs uppercase tracking-widest-plus text-[#9BA595] mb-2">Add your review</div>
              <input
                type="text"
                placeholder="Your name"
                value={reviewForm.name}
                onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#D27D60] transition-colors"
                data-testid="review-name-input"
              />
              <div className="flex gap-1">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, rating: n })}
                    data-testid={`review-star-${n}`}
                  >
                    <Star className={`w-6 h-6 ${n <= reviewForm.rating ? "fill-[#D27D60] text-[#D27D60]" : "text-[#E3DCD2]"} hover:text-[#D27D60] transition-colors`} />
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Title (optional)"
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#D27D60] transition-colors"
                data-testid="review-title-input"
              />
              <textarea
                placeholder="Share your experience..."
                rows={4}
                value={reviewForm.comment}
                onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#D27D60] transition-colors resize-none"
                data-testid="review-comment-input"
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-2.5 rounded-full text-xs uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors disabled:opacity-50"
                data-testid="review-submit-btn"
              >
                {submitting ? "Sending..." : "Submit review"}
              </button>
            </form>
          </div>

          <div className="md:col-span-7 space-y-6">
            {reviews.length === 0 ? (
              <div className="text-sm text-[#9BA595]">Be the first to review this product.</div>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="pb-6 border-b border-[#E3DCD2] last:border-0" data-testid={`review-item-${r.id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex gap-0.5">
                      {[0,1,2,3,4].map(i => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#D27D60] text-[#D27D60]" : "text-[#E3DCD2]"}`} />
                      ))}
                    </div>
                    <span className="text-xs font-medium">{r.name}</span>
                    <span className="text-xs text-[#9BA595]">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.title && <div className="font-serif-display text-xl mb-1">{r.title}</div>}
                  <p className="text-sm text-[#4a4a4a] leading-relaxed">{r.comment}</p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
